import { Paper, Box, Typography, IconButton, Menu, MenuItem, CircularProgress } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import EditIcon from '@mui/icons-material/Edit';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { Home } from '../types/homes';
import { useState } from 'react';
// Import the home placeholder image
import homePlaceholder from '../assets/homeplaceholderimage.jpg';

interface HomeCardProps {
  home: Home;
  onDelete: (id: string) => void;
  onEdit: (id: string) => void;
  onClick: (id: string) => void;
  isDeleting?: boolean;
}

const HomeCard = ({ home, onDelete, onEdit, onClick, isDeleting = false }: HomeCardProps) => {
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(menuAnchorEl);
  
  // Parse details as JSON, fallback to empty object if invalid
  let parsedDetails = { address: '', bedrooms: 3, bathrooms: 2, sqft: 1800, price: 0 };
  try {
    if (typeof home.details === 'string') {
      parsedDetails = JSON.parse(home.details);
    }
  } catch (e) {
    // If not valid JSON, use details as address for backward compatibility
    parsedDetails.address = home.details;
  }
  
  // Extract values from parsed details
  const { address, bedrooms, bathrooms, sqft, price } = parsedDetails;
  
  // Determine which image to display - use primary_image_url if available
  const imageUrl = home.primary_image_url || homePlaceholder;
  
  // Calculate task completion percentage - using snake_case only
  const tasksCount = home.tasks_count || 0;
  const completedTasksCount = home.completed_tasks_count || 0;
  const taskCompletionPercentage = tasksCount > 0 
    ? Math.round((completedTasksCount / tasksCount) * 100) 
    : 0;
  

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
  };
  
  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };
  
  const handleDelete = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    onDelete(home.id);
    handleMenuClose();
  };

  const handleEdit = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    onEdit(home.id);
    handleMenuClose();
  };
  
  return (
    <Paper
      elevation={3}
      data-testid="home-card"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        opacity: isDeleting ? 0.3 : 1,
        position: 'relative',
        borderRadius: 2,
        overflow: 'hidden',
        maxWidth: { xs: '100%', sm: '380px' },
        transition: 'all 0.3s ease',
        filter: isDeleting ? 'grayscale(100%)' : 'none',
        transform: isDeleting ? 'scale(0.95)' : 'scale(1)',
        border: isDeleting ? '3px solid #f44336' : 'none',
        '&:hover': {
          transform: isDeleting ? 'scale(0.95)' : 'scale(1.02)',
          boxShadow: isDeleting ? 3 : 6,
          cursor: isDeleting ? 'default' : 'pointer'
        }
      }}
      onClick={() => !isDeleting && onClick(home.id)}
    >
      {/* Add an overlay with spinner when deleting */}
      {isDeleting && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
            zIndex: 1,
          }}
        >
          <CircularProgress size={50} />
          <Typography 
            variant="subtitle1" 
            sx={{ mt: 2, fontWeight: 'medium', color: 'text.primary' }}
          >
            Deleting...
          </Typography>
        </Box>
      )}
      
      {/* Image with primary image or placeholder */}
      <Box 
        sx={{ 
          height: '180px',
          backgroundColor: 'grey.300',
          position: 'relative',
          backgroundImage: `url(${imageUrl})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <IconButton
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.7)',
            }
          }}
          onClick={handleMenuOpen}
        >
          <MoreVertIcon sx={{ color: 'white' }} />
        </IconButton>
        <Menu
          anchorEl={menuAnchorEl}
          open={menuOpen}
          onClose={handleMenuClose}
          onClick={(e) => e.stopPropagation()}
          slotProps={{
            paper: {
              elevation: 2,
              sx: {
                overflow: 'visible',
                mt: 1.5
              }
            }
          }}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        >
          <MenuItem onClick={handleEdit}>
            <EditIcon fontSize="small" sx={{ mr: 1 }} color="primary" />
            <Typography variant="body2">Edit</Typography>
          </MenuItem>
          <MenuItem onClick={handleDelete}>
            <DeleteIcon fontSize="small" sx={{ mr: 1 }} color="error" />
            <Typography variant="body2">Delete</Typography>
          </MenuItem>
        </Menu>
      </Box>
      
      {/* Home information section */}
      <Box sx={{ p: 1.5, flexGrow: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
          <Typography 
            variant="h6" 
            component="h3" 
            sx={{ 
              fontWeight: 'bold', 
              textAlign: 'left',
              mr: 1,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              fontSize: '1.1rem'
            }}
          >
            {home.name}
          </Typography>
          <Typography 
            variant="subtitle1" 
            sx={{ 
              fontWeight: 'bold', 
              color: 'primary.main', 
              textAlign: 'right',
              whiteSpace: 'nowrap',
              fontSize: '0.95rem'
            }}
          >
            {price ? `$${price.toLocaleString()}` : 'Price not available'}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
          <LocationOnIcon 
            sx={{ 
              fontSize: 16, 
              color: 'text.secondary',
              mr: 0.5
            }} 
          />
          <Typography 
            variant="body2" 
            color="text.secondary"
            sx={{ 
              textAlign: 'left',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}
          >
            {address || 'Address not available'}
          </Typography>
        </Box>
        
        {/* Property details with task completion info */}
        <Box 
          sx={{ 
            mt: 0.5, 
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          <Typography 
            variant="body2" 
            color="text.secondary" 
            sx={{ 
              textAlign: 'left',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}
          >
            {`${bedrooms} bed • ${bathrooms} bath • ${sqft} sqft`}
          </Typography>
          
          {tasksCount > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', ml: 1 }}>
              <CheckCircleIcon 
                sx={{ 
                  fontSize: 14, 
                  color: taskCompletionPercentage === 100 ? 'success.main' : 'primary.main',
                  mr: 0.5
                }} 
              />
              <Typography variant="body2" color="text.secondary" fontSize="0.8rem" whiteSpace="nowrap">
                {`${completedTasksCount}/${tasksCount} (${taskCompletionPercentage}%)`}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Paper>
  );
};

export default HomeCard;